import React, { useMemo, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { detachAnalyticsTemplateRoutine } from 'features/analytics/ducks/actions'
import { Collapse, Skeleton } from '@mui/material'
import { TEMPLATE_ITEMS_NAMES } from 'utils/constants'
import { pick } from 'ramda'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import { checkAnalyticsCondition } from 'utils/analytics'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined'
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import MenuItem from '@mui/material/MenuItem'
import Menu from '@mui/material/Menu'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined'
import EditGeneralInfo from 'features/bpDetails/Components/edit/EditGeneralInfo'
import EditAddress from 'features/bpDetails/Components/edit/EditAddress'
import EditBuildingTypes from 'features/bpDetails/Components/edit/EditBuildingTypes'
import Modal from 'components/atoms/Modal'
import AddAssignmentModal from 'features/accessControl/components/AddAssignmentModal'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { canEditBp } from 'utils/user'
import { getSelectedBp } from 'features/bpDetails/ducks/selectors'
import { selectBpUnits } from 'ducks/units/selectors'
import AddMultipleFilesModal from 'features/bpDetails/Components/files/AddMultipleFilesModal'
import { useParams } from 'react-router'
import { selectBpFilesList } from 'ducks/files/selectors'
import AssignChecklistItemModal from 'features/analytics/compontents/AssignChecklistItemModal'
import ChecklistItemTagsPreviewModal from 'features/analytics/compontents/ChecklistItemTagsPreviewModal'
import ChecklistOverviewSummary from 'features/analytics/compontents/ChecklistOverviewSummary'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import AttachTagsToUploadingFile from 'features/bpDetails/Components/files/AttachTagsToUploadingFiles'
import { getFileTagsForBpRoutine } from 'ducks/files/actions'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined'
import { getChecklistItemOverview } from 'services/taskService'
import AlreadyUploadedFilesOfTypeModal from 'features/analytics/compontents/AlreadyUploadedFilesOfTypeModal'
import { scoreColour } from 'features/dashboard/charts/scoreColour'

const color = scoreColour

const getChecklistItemTagNames = item => {
  const requirements =
    item?.bpTagRequirements ||
    item?.bp_tag_requirements ||
    item?.tagRequirements ||
    item?.tag_requirements ||
    []

  return Array.from(
    new Set(
      requirements
        .map(
          tag =>
            tag?.fileTag?.name || tag?.file_tag?.name || tag?.tag_value || null
        )
        .filter(Boolean)
    )
  )
}

const getChecklistGroupNumbers = (item, overviewEntries = []) => {
  const groupNos = new Set()
  const requirements =
    item?.bp_tag_requirements ||
    item?.bpTagRequirements ||
    item?.tag_requirements ||
    item?.tagRequirements ||
    []
  const assignments = item?.assignments || []

  overviewEntries.forEach(entry => {
    const groupNo = Number(entry?.groupNo ?? entry?.group_no)
    if (!Number.isNaN(groupNo)) groupNos.add(groupNo)
  })

  requirements.forEach(requirement => {
    const groupNo = Number(requirement?.group_no ?? requirement?.groupNo)
    if (!Number.isNaN(groupNo)) groupNos.add(groupNo)
  })

  assignments.forEach(assignment => {
    const groupNo = Number(assignment?.group_no ?? assignment?.groupNo)
    if (!Number.isNaN(groupNo)) groupNos.add(groupNo)
  })

  return Array.from(groupNos).sort((a, b) => a - b)
}

const getChecklistProgress = (filesCount, totalRulesCount) => {
  if (totalRulesCount > 0) {
    return (Math.min(filesCount, totalRulesCount) / totalRulesCount) * 100
  }

  return filesCount > 0 ? 100 : 0
}

const getTemplateFileTypes = template =>
  template?.fileTypes || template?.file_types || []

export const getTemplatePercentage = (template, bp, bpFiles = []) => {
  const rawPercentage =
    template?.percentageValue ??
    template?.percentage_value ??
    template?.percentage ??
    template?.pivot?.percentageValue ??
    template?.pivot?.percentage_value ??
    template?.pivot?.percentage ??
    0

  const numericPercentage = Number(rawPercentage)
  const normalizedRaw = Number.isFinite(numericPercentage)
    ? Math.max(0, Math.min(numericPercentage, 100))
    : 0

  if (normalizedRaw > 0) return normalizedRaw

  let score = 0
  let total = 0
  const pass = condition => {
    total += 1
    if (condition) score += 1
  }

  if (template?.hasUprn) pass(Boolean(bp?.uprn))
  if (template?.hasAddress) pass(Boolean(bp?.street && bp?.city && bp?.postalCode))
  if (template?.hasOwner) pass((bp?.owners || []).length > 0)
  if (template?.hasAgent) pass((bp?.agents || []).length > 0)
  if (template?.hasBuildingTypes) pass((bp?.buildingTypes || bp?.building_types || []).length > 0)

  getTemplateFileTypes(template).forEach(fileType => {
    const fileTypeId = fileType?.id
    const hasFile =
      Boolean(fileType?.fileExists ?? fileType?.file_exists) ||
      Number(fileType?.filesCount ?? fileType?.files_count ?? 0) > 0 ||
      (bpFiles || []).some(
        file =>
          !file?.deletedAt &&
          !file?.deleted_at &&
          String(file?.fileTypeId || file?.file_type_id) === String(fileTypeId)
      )

    pass(hasFile)
  })

  if (total === 0) return normalizedRaw

  return Math.max(0, Math.min(Math.round((score / total) * 100), 100))
}

const isChecklistEntryUploaded = entry => {
  const files = entry?.uploadedFiles || entry?.uploaded_files || []
  const taskStatus = String(entry?.task?.status || '').toLowerCase()

  return (
    Boolean(entry?.hasUploadedFile || entry?.has_uploaded_file) ||
    files.length > 0 ||
    taskStatus === 'completed'
  )
}

const appendIfPresent = (params, key, value) => {
  if (value === undefined || value === null || value === '') return
  params.append(key, value)
}

const getEntryAssigneeIds = entry =>
  [
    ...(entry?.assignee_user_ids || []),
    entry?.assignee_user_id,
    entry?.assigneeUserId,
    ...(entry?.assignees || []).map(assignee => assignee?.id),
    entry?.assignee?.id
  ]
    .filter(Boolean)
    .map(String)

const getAssigneeDisplayName = assignee =>
  assignee?.label ||
  [assignee?.firstName || assignee?.first_name, assignee?.lastName || assignee?.last_name]
    .filter(Boolean)
    .join(' ') ||
  assignee?.email ||
  ''

const getEntryAssigneeUsers = entry =>
  [
    ...(entry?.assignees || []),
    entry?.assignee
  ]
    .filter(assignee => assignee?.id)
    .map(assignee => {
      const label = getAssigneeDisplayName(assignee)
      const [firstName, ...lastNameParts] = String(label || '').split(' ')

      return {
        id: String(assignee.id),
        firstName:
          assignee.firstName ||
          assignee.first_name ||
          firstName ||
          assignee.email ||
          'User',
        lastName:
          assignee.lastName ||
          assignee.last_name ||
          lastNameParts.join(' '),
        email: assignee.email || '',
        label
      }
    })

const mergeUniqueById = items =>
  Array.from(
    (items || []).reduce((acc, item) => {
      if (item?.id) acc.set(String(item.id), item)
      return acc
    }, new Map()).values()
  )

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i
const SHOULD_LOG_CHECKLIST_TASK_DEBUG = process.env.NODE_ENV !== 'production'

const getRawUnitName = unit =>
  String(unit?.name || unit?.unit_name || unit?.unitName || '').trim()

const getUnitId = unit =>
  String(unit?.id || unit?.building_unit_passport_id || unit?.buildingUnitPassportId || '')

const getEntryUnit = entry => ({
  ...(entry?.unit || {}),
  id:
    entry?.unit?.id ||
    entry?.unit?.building_unit_passport_id ||
    entry?.unit?.buildingUnitPassportId ||
    entry?.building_unit_passport_id ||
    entry?.buildingUnitPassportId ||
    entry?.task?.building_unit_passport_id ||
    entry?.task?.buildingUnitPassportId ||
    ''
})

const getFriendlyUnitName = (unit, index) => {
  const rawName = getRawUnitName(unit)

  if (rawName && !UUID_PATTERN.test(rawName)) {
    return rawName
  }

  return `Unit ${index + 1}`
}

const getUnitDisplayName = (unit, index, unitNameById = {}) => {
  const unitId = getUnitId(unit)
  const syncedName = unitId ? unitNameById[unitId] : ''

  if (syncedName) {
    return syncedName
  }

  return getFriendlyUnitName(unit, index)
}

const isActiveUnitForPicker = (unit, unitNameById = {}) => {
  const activeUnitIdsCount = Object.keys(unitNameById || {}).length
  const unitId = getUnitId(unit)

  return Boolean(unitId) && (
    activeUnitIdsCount === 0 ||
    Boolean(unitNameById[unitId])
  )
}

const sortByUnitName = (a, b) =>
  String(a.name || '').localeCompare(String(b.name || ''), undefined, {
    numeric: true,
    sensitivity: 'base'
  })

const buildUnitDebugRow = ({ entry, index, unitNameById }) => {
  const unit = getEntryUnit(entry)
  const unitId = getUnitId(unit)
  const overviewName = getRawUnitName(unit)
  const syncedName = unitId ? unitNameById?.[unitId] || '' : ''
  const resolvedName = getUnitDisplayName(unit, index, unitNameById)
  const isActive = isActiveUnitForPicker(unit, unitNameById)

  return {
    index: index + 1,
    unitId,
    groupNo: entry?.groupNo ?? entry?.group_no ?? 0,
    scope: entry?.scope,
    overviewName,
    syncedName,
    resolvedName,
    usedSource: syncedName ? 'bpUnits' : 'overview/fallback',
    isActive,
    isDeleted: Boolean(unit?.is_deleted || unit?.deleted_at),
    assignee:
      entry?.assignee?.label ||
      entry?.assignee?.email ||
      (entry?.assignees || [])
        .map(assignee => assignee?.label || assignee?.email)
        .filter(Boolean)
        .join(', ') ||
      null
  }
}

const ChecklistTaskTrigger = ({
  rules,
  bpId,
  bpName,
  categoryId,
  typeId,
  template,
  item,
  displayName,
  overviewEntries,
  unitNameById,
  activeUnits
}) => {
  const [open, setOpen] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)
  const [unitSearch, setUnitSearch] = useState('')
  const [selectedUnitIds, setSelectedUnitIds] = useState([])
  const lastUnitResolutionLogKeyRef = useRef('')

  const entries = overviewEntries || []
  const templateId = template?.id
  const itemId = item?.id
  const templateName = template?.name || ''
  const itemTitle = item?.title || displayName
  const itemDescription = item?.description || ''

  const buildCreateTaskUrl = (rule = null, unit = null, units = []) => {
    const params = new URLSearchParams()
    const titleParts = ['Upload', itemTitle]
    const selectedUnits = unit?.id
      ? [unit]
      : (Array.isArray(units) ? units : []).filter(selectedUnit => selectedUnit?.id)
    const selectedUnitIdsForTask = selectedUnits.map(selectedUnit => selectedUnit.id)

    if (selectedUnits.length === 1 && selectedUnits[0]?.name) {
      titleParts.push(`(${selectedUnits[0].name})`)
    } else if (selectedUnits.length > 1) {
      titleParts.push(`(${selectedUnits.length} units)`)
    }

    appendIfPresent(params, 'checklist', '1')
    appendIfPresent(params, 'title', titleParts.join(' '))
    appendIfPresent(
      params,
      'description',
      [
        templateName ? `Assigned from checklist template "${templateName}".` : null,
        itemDescription ? itemDescription.trim() : null,
        'Upload the required file to complete this checklist item.'
      ].filter(Boolean).join('\n\n')
    )
    appendIfPresent(params, 'bpId', bpId)
    appendIfPresent(params, 'bpName', bpName)
    appendIfPresent(params, 'templateId', templateId)
    appendIfPresent(params, 'templateName', templateName)
    appendIfPresent(params, 'itemId', itemId)
    appendIfPresent(params, 'itemTitle', itemTitle)
    appendIfPresent(params, 'fileTypeId', typeId)
    appendIfPresent(params, 'fileCategoryId', categoryId)
    appendIfPresent(params, 'groupNo', rule?.groupNo ?? 0)
    appendIfPresent(params, 'assignmentScope', selectedUnitIdsForTask.length > 0 ? 'unit' : 'bp')
    if (selectedUnitIdsForTask.length === 1) {
      appendIfPresent(params, 'unitId', selectedUnitIdsForTask[0])
      appendIfPresent(params, 'unitName', selectedUnits[0]?.name)
    } else if (selectedUnitIdsForTask.length > 1) {
      params.append('unitIds', selectedUnitIdsForTask.join(','))
      params.append('unitMode', 'fan_out')
    }

    const tagIds = rule?.tagIds || []
    if (tagIds.length > 0) params.append('tagIds', tagIds.join(','))

    const unitAssigneeIds = selectedUnits.flatMap(selectedUnit => selectedUnit?.assigneeIds || [])
    const assigneeIds = Array.from(
      new Set(unitAssigneeIds.length > 0 ? unitAssigneeIds : rule?.assigneeIds || [])
    )
    if (assigneeIds.length > 0) {
      params.append('assigneeIds', assigneeIds.join(','))
    }

    const unitAssigneeUsers = selectedUnits.flatMap(selectedUnit => selectedUnit?.assigneeUsers || [])
    const assigneeUsers = mergeUniqueById(
      unitAssigneeUsers.length > 0 ? unitAssigneeUsers : rule?.assigneeUsers || []
    )
    if (assigneeUsers.length > 0) {
      params.append('assigneeUsers', JSON.stringify(assigneeUsers))
    }

    return `${PRIVATE_PATHS.createTask}?${params.toString()}`
  }

  const openCreateTask = (rule = null, unit = null, units = []) => {
    redirect(buildCreateTaskUrl(rule, unit, units))
  }

  const unitsForRule = useMemo(() => {
    if (!selectedRule) return []
    const gNo = selectedRule.groupNo
    const ruleEntries = entries
      .filter(entry => {
        const entryGroupNo = entry?.groupNo ?? entry?.group_no ?? 0
        return Number(entryGroupNo) === Number(gNo) &&
          entry?.scope === 'unit' &&
          getUnitId(getEntryUnit(entry))
      })

    if (activeUnits?.length > 0) {
      const entriesByUnitId = ruleEntries.reduce((acc, entry) => {
        acc[getUnitId(getEntryUnit(entry))] = entry
        return acc
      }, {})

      return activeUnits
        .map((unit, index) => {
          const entry = entriesByUnitId[String(unit.id)] || null
          const assigneeIds = entry
            ? getEntryAssigneeIds(entry)
            : selectedRule?.assigneeIds || []
          const assigneeUsers = entry
            ? getEntryAssigneeUsers(entry)
            : selectedRule?.assigneeUsers || []

          return {
            id: String(unit.id),
            name: unit.name || getUnitDisplayName(unit, index, unitNameById),
            uprn: unit.uprn || '',
            buildingType: unit.buildingType || unit.building_type || '',
            isActive: true,
            isDeleted: false,
            assigneeIds,
            assigneeUsers,
            assignee: entry
              ? entry?.assignee?.label ||
                entry?.assignee?.email ||
                (entry?.assignees || [])
                  .map(assignee => assignee?.label || assignee?.email)
                  .filter(Boolean)
                  .join(', ') ||
                null
              : null,
            hasTask: Boolean(entry?.task?.id)
          }
        })
        .sort(sortByUnitName)
    }

    return ruleEntries
      .map((entry, index) => {
        const unit = getEntryUnit(entry)

        return {
          id: getUnitId(unit),
          name: getUnitDisplayName(unit, index, unitNameById),
          uprn: unit?.uprn || '',
          buildingType: unit?.buildingType || unit?.building_type || '',
          isActive: isActiveUnitForPicker(unit, unitNameById),
          isDeleted: Boolean(unit?.is_deleted || unit?.deleted_at),
          assigneeIds: getEntryAssigneeIds(entry),
          assigneeUsers: getEntryAssigneeUsers(entry),
          assignee:
            entry?.assignee?.label ||
            entry?.assignee?.email ||
            (entry?.assignees || [])
              .map(assignee => assignee?.label || assignee?.email)
              .filter(Boolean)
              .join(', ') ||
            null,
          hasTask: Boolean(entry?.task?.id)
        }
      })
      .sort(sortByUnitName)
  }, [activeUnits, entries, selectedRule, unitNameById])

  const activeUnitIds = useMemo(
    () => unitsForRule.filter(unit => unit.isActive).map(unit => unit.id),
    [unitsForRule]
  )
  const activeUnitIdsKey = activeUnitIds.join('|')

  useEffect(() => {
    setSelectedUnitIds(prev => {
      const activeIdSet = new Set(activeUnitIds)
      const next = prev.filter(unitId => activeIdSet.has(unitId))
      return next.length === prev.length ? prev : next
    })
  }, [activeUnitIdsKey])

  useEffect(() => {
    setUnitSearch('')
    setSelectedUnitIds([])
  }, [selectedRule?.groupNo])

  const selectedUnitIdSet = useMemo(
    () => new Set(selectedUnitIds),
    [selectedUnitIds]
  )

  const filteredUnitsForRule = useMemo(() => {
    const search = unitSearch.trim().toLowerCase()
    const matchingUnits = search
      ? unitsForRule.filter(unit =>
        [
          unit.name,
          unit.assignee,
          unit.uprn,
          unit.buildingType,
          unit.id
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(search))
      )
      : unitsForRule

    return [...matchingUnits].sort((a, b) => {
      const aSelected = selectedUnitIdSet.has(a.id)
      const bSelected = selectedUnitIdSet.has(b.id)
      if (aSelected !== bSelected) return aSelected ? -1 : 1
      return sortByUnitName(a, b)
    })
  }, [selectedUnitIdSet, unitSearch, unitsForRule])
  const selectableFilteredUnitIds = useMemo(
    () => filteredUnitsForRule.filter(unit => unit.isActive).map(unit => unit.id),
    [filteredUnitsForRule]
  )
  const selectedUnitsForCreate = useMemo(
    () => unitsForRule.filter(unit => selectedUnitIdSet.has(unit.id) && unit.isActive),
    [selectedUnitIdSet, unitsForRule]
  )
  const allFilteredUnitsSelected = selectableFilteredUnitIds.length > 0 &&
    selectableFilteredUnitIds.every(unitId => selectedUnitIdSet.has(unitId))

  const toggleUnitSelection = unit => {
    if (!unit?.isActive) return

    setSelectedUnitIds(prev =>
      prev.includes(unit.id)
        ? prev.filter(unitId => unitId !== unit.id)
        : [...prev, unit.id]
    )
  }

  const selectAllFilteredUnits = () => {
    if (selectableFilteredUnitIds.length === 0) return

    setSelectedUnitIds(prev =>
      Array.from(new Set([...prev, ...selectableFilteredUnitIds]))
    )
  }

  const deselectFilteredUnits = () => {
    if (selectableFilteredUnitIds.length === 0) return

    const filteredIdSet = new Set(selectableFilteredUnitIds)
    setSelectedUnitIds(prev => prev.filter(unitId => !filteredIdSet.has(unitId)))
  }

  const createSelectedUnitTasks = () => {
    if (selectedUnitsForCreate.length === 0) return

    if (selectedUnitsForCreate.length === 1) {
      openCreateTask(selectedRule, selectedUnitsForCreate[0])
      return
    }

    openCreateTask(selectedRule, null, selectedUnitsForCreate)
  }

  useEffect(() => {
    if (!SHOULD_LOG_CHECKLIST_TASK_DEBUG || !open || !selectedRule) return

    const gNo = selectedRule.groupNo
    const matchingEntries = entries.filter(entry => {
      const entryGroupNo = entry?.groupNo ?? entry?.group_no ?? 0
      return Number(entryGroupNo) === Number(gNo) && entry?.scope === 'unit'
    })
    const debugRows = matchingEntries.map((entry, index) =>
      buildUnitDebugRow({ entry, index, unitNameById })
    )
    const logKey = JSON.stringify(debugRows.map(row => [
      row.unitId,
      row.overviewName,
      row.syncedName,
      row.resolvedName,
      row.usedSource
    ]))

    if (lastUnitResolutionLogKeyRef.current === logKey) return
    lastUnitResolutionLogKeyRef.current = logKey

    console.groupCollapsed('[Checklist Create Task] Unit name resolution')
    console.log('context', {
      bpId,
      bpName,
      templateId,
      itemId,
      itemTitle,
      selectedRule,
      overviewEntriesCount: entries.length,
      bpUnitsCount: Object.keys(unitNameById || {}).length
    })
    console.log('bp unit names by id', unitNameById)
    console.log('active bp units used by picker', activeUnits)
    console.table(debugRows)
    console.groupEnd()
  }, [
    activeUnits,
    bpId,
    bpName,
    entries,
    itemId,
    itemTitle,
    open,
    selectedRule,
    templateId,
    unitNameById
  ])

  const hasBpScope = useMemo(() => {
    if (!selectedRule) return false
    const gNo = selectedRule.groupNo

    return entries.some(entry => {
      const entryGroupNo = entry?.groupNo ?? entry?.group_no ?? 0
      return Number(entryGroupNo) === Number(gNo) && entry?.scope !== 'unit'
    })
  }, [entries, selectedRule])

  const handleRuleClick = rule => {
    const ruleEntries = entries.filter(entry => {
      const entryGroupNo = entry?.groupNo ?? entry?.group_no ?? 0
      return Number(entryGroupNo) === Number(rule.groupNo)
    })
    const unitEntries = ruleEntries.filter(
      entry => entry?.scope === 'unit' && getUnitId(getEntryUnit(entry))
    )

    if (SHOULD_LOG_CHECKLIST_TASK_DEBUG) {
      console.groupCollapsed('[Checklist Create Task] Rule selected')
      console.log('rule', rule)
      console.log('rule entries count', ruleEntries.length)
      console.table(
        ruleEntries.slice(0, 30).map((entry, index) =>
          buildUnitDebugRow({ entry, index, unitNameById })
        )
      )
      console.groupEnd()
    }

    if (unitEntries.length > 0) {
      setSelectedRule(rule)
      return
    }

    const bpEntry = ruleEntries.find(entry => entry?.scope !== 'unit')
    openCreateTask({
      ...rule,
      assigneeIds: getEntryAssigneeIds(bpEntry),
      assigneeUsers: getEntryAssigneeUsers(bpEntry)
    })
  }

  const handleClick = e => {
    e.stopPropagation()
    if (rules.length > 0) {
      setOpen(true)
      return
    }

    openCreateTask()
  }

  const handleClose = () => {
    setSelectedRule(null)
    setUnitSearch('')
    setSelectedUnitIds([])
    setOpen(false)
  }

  return (
    <>
      <CreateTaskTrigger
        type='button'
        aria-label={`Create task for ${displayName}`}
        title='Create task'
        onClick={handleClick}
      >
        <CreateTaskIcon />
      </CreateTaskTrigger>
      {open && (
        <Modal
          open={open}
          title={selectedRule
            ? `Create task for ${itemTitle}`
            : `Create task for ${displayName}`
          }
          onClose={handleClose}
          minWidthDesktop={selectedRule ? '760px' : undefined}
          maxWidthDesktop={selectedRule ? '840px' : undefined}
        >
          <RulePickerContent>
            {selectedRule ? (
              <>
                <RulePickerHint>
                  One file is required per unit. Select the unit or units that should receive the task. Selected units are shown first.
                </RulePickerHint>
                <BackButton type='button' onClick={() => setSelectedRule(null)}>
                  Choose another rule
                </BackButton>
                {hasBpScope && (
                  <RulePickerCard
                    type='button'
                    onClick={() => openCreateTask(selectedRule)}
                  >
                    <RulePickerIndex>BP</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>Whole Building Passport</RulePickerName>
                    </RulePickerInfo>
                    <CreateTaskIcon />
                  </RulePickerCard>
                )}
                {unitsForRule.length > 0 && (
                  <RulePickerToolbar>
                    <RulePickerSearchInput
                      type='search'
                      value={unitSearch}
                      onChange={e => setUnitSearch(e.target.value)}
                      placeholder='Search units...'
                      aria-label='Search units'
                    />
                    <RulePickerBulkActions>
                      <RulePickerSelectedCount>
                        {selectedUnitsForCreate.length} selected
                      </RulePickerSelectedCount>
                      <RulePickerUtilityButton
                        type='button'
                        onClick={selectAllFilteredUnits}
                        disabled={selectableFilteredUnitIds.length === 0 || allFilteredUnitsSelected}
                      >
                        Select all
                      </RulePickerUtilityButton>
                      <RulePickerUtilityButton
                        type='button'
                        onClick={deselectFilteredUnits}
                        disabled={selectableFilteredUnitIds.length === 0 || selectedUnitsForCreate.length === 0}
                      >
                        Deselect all
                      </RulePickerUtilityButton>
                    </RulePickerBulkActions>
                  </RulePickerToolbar>
                )}
                {filteredUnitsForRule.length > 0 && (
                  <RulePickerUnitGrid>
                    {filteredUnitsForRule.map((unit, index) => {
                      const isSelected = selectedUnitIdSet.has(unit.id)

                      return (
                        <RulePickerCard
                          key={unit.id}
                          type='button'
                          onClick={() => toggleUnitSelection(unit)}
                          disabled={!unit.isActive}
                          $selected={isSelected}
                        >
                          <RulePickerIndex>{index + 1}</RulePickerIndex>
                          <RulePickerInfo>
                            <RulePickerName>{unit.name}</RulePickerName>
                            {!unit.isActive ? (
                              <RulePickerState>Removed unit</RulePickerState>
                            ) : unit.assignee && (
                              <RulePickerState>{unit.assignee}</RulePickerState>
                            )}
                          </RulePickerInfo>
                          <RulePickerSelection $selected={isSelected}>
                            {isSelected ? <CheckBoxOutlinedIcon /> : <CheckBoxOutlineBlankOutlinedIcon />}
                          </RulePickerSelection>
                        </RulePickerCard>
                      )
                    })}
                  </RulePickerUnitGrid>
                )}
                {unitsForRule.length === 0 && (
                  <RulePickerHint>
                    No active units are assigned to this rule. Use Settings to update the checklist assignments.
                  </RulePickerHint>
                )}
                {unitsForRule.length > 0 && filteredUnitsForRule.length === 0 && (
                  <RulePickerHint>
                    No units match your search.
                  </RulePickerHint>
                )}
                {unitsForRule.length > 0 && (
                  <RulePickerFooter>
                    <RulePickerSelectedCount>
                      {selectedUnitsForCreate.length} unit{selectedUnitsForCreate.length !== 1 ? 's' : ''} selected
                    </RulePickerSelectedCount>
                    <RulePickerPrimaryButton
                      type='button'
                      onClick={createSelectedUnitTasks}
                      disabled={selectedUnitsForCreate.length === 0}
                    >
                      <CreateTaskIcon />
                      {selectedUnitsForCreate.length > 1
                        ? `Create ${selectedUnitsForCreate.length} tasks`
                        : 'Create task'}
                    </RulePickerPrimaryButton>
                  </RulePickerFooter>
                )}
              </>
            ) : (
              <>
                <RulePickerHint>
                  Choose whether the upload requires one file for the whole Building Passport or one file per unit (select below). You can create multiple rules for the same file upload—for example, one Asbestos Survey tagged “South Block” and another tagged “North Block”. Choose which rule the task should follow.
                </RulePickerHint>
                {rules.map((rule, index) => (
                  <RulePickerCard
                    key={rule.groupNo}
                    type='button'
                    onClick={() => handleRuleClick(rule)}
                  >
                    <RulePickerIndex>{index + 1}</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>
                        {rule.ruleName || `Rule ${index + 1}`}
                      </RulePickerName>
                      {rule.tagNames.length > 0 && (
                        <RulePickerTags>
                          {rule.tagNames.map(tag => (
                            <RulePickerTag key={tag}>{tag}</RulePickerTag>
                          ))}
                        </RulePickerTags>
                      )}
                    </RulePickerInfo>
                    <CreateTaskIcon />
                  </RulePickerCard>
                ))}
              </>
            )}
          </RulePickerContent>
        </Modal>
      )}
    </>
  )
}

const RulePickerUpload = ({
  rules,
  bpId,
  categoryId,
  typeId,
  templateId,
  itemId,
  displayName,
  onOverviewLoaded,
  overviewEntries: externalEntries,
  unitNameById
}) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedRule, setSelectedRule] = useState(null)
  const [overviewData, setOverviewData] = useState(null)

  const entries =
    overviewData?.checklistOverview?.entries ||
    overviewData?.checklist_overview?.entries ||
    externalEntries ||
    []
  const isRefreshingOverview = Boolean(itemId && loading && !overviewData)

  useEffect(() => {
    let active = true

    if (!open || !itemId) return undefined

    setLoading(true)
    getChecklistItemOverview({ bpId, templateId, itemId })
      .then(({ data }) => {
        if (!active) return
        const item = data?.data || null
        setOverviewData(item)
        if (onOverviewLoaded) onOverviewLoaded(item)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [open, itemId, bpId, templateId])

  const unitsForRule = useMemo(() => {
    if (!selectedRule) return []
    const gNo = selectedRule.groupNo
    return entries
      .filter(e => {
        const entryGroupNo = e?.groupNo ?? e?.group_no ?? 0
        return Number(entryGroupNo) === Number(gNo) &&
          e?.scope === 'unit' &&
          getUnitId(getEntryUnit(e))
      })
      .map((e, index) => {
        const unit = getEntryUnit(e)

        return {
          id: getUnitId(unit),
          name: getUnitDisplayName(unit, index, unitNameById),
          isActive: isActiveUnitForPicker(unit, unitNameById),
          isDeleted: Boolean(unit?.is_deleted || unit?.deleted_at),
          uploaded: !!(e?.hasUploadedFile || e?.has_uploaded_file),
          assignee: e?.assignee?.label || e?.assignee?.email || null
        }
      })
      .sort(sortByUnitName)
  }, [selectedRule, entries, unitNameById])

  const hasBpScope = useMemo(() => {
    if (!selectedRule) return false
    const gNo = selectedRule.groupNo
    return entries.some(e => {
      const entryGroupNo = e?.groupNo ?? e?.group_no ?? 0
      return Number(entryGroupNo) === Number(gNo) && e?.scope !== 'unit'
    })
  }, [selectedRule, entries])

  const handleRuleClick = rule => {
    if (rule.disabled) return
    if (isRefreshingOverview) return

    const gNo = rule.groupNo
    const ruleEntries = entries.filter(e => {
      const entryGroupNo = e?.groupNo ?? e?.group_no ?? 0
      return Number(entryGroupNo) === Number(gNo) && e?.scope === 'unit' && e?.unit?.id
    })

    if (ruleEntries.length > 0) {
      setSelectedRule(rule)
    } else if (rule.uploadUrl) {
      try {
        const url = new URL(rule.uploadUrl, window.location.origin)
        url.searchParams.set('method', 'traditional')
        window.open(url.toString(), '_blank')
      } catch {
        window.open(rule.uploadUrl, '_blank')
      }
      setOpen(false)
      return
    } else {
      openUploadPage(rule, null)
    }
  }

  const openUploadPage = (rule, unitId) => {
    const params = new URLSearchParams({ method: 'traditional' })
    if (categoryId) params.append('category', categoryId)
    if (typeId) params.append('type', typeId)
    if (templateId) params.append('templateId', templateId)
    if (itemId) params.append('itemId', itemId)
    if (rule.taskId) params.append('taskId', rule.taskId)
    if (rule.tagIds && rule.tagIds.length > 0) {
      params.append('tags', rule.tagIds.join(','))
    }
    if (unitId) params.append('unit', unitId)

    window.open(`/files/upload/${bpId}?${params.toString()}`, '_blank')
    setSelectedRule(null)
    setOpen(false)
  }

  const handleBack = () => setSelectedRule(null)

  const handleClose = () => {
    setSelectedRule(null)
    setOpen(false)
  }

  return (
    <>
      <AddFilesTrigger
        type='button'
        aria-label={`Add files for ${displayName}`}
        title='Add files'
        onClick={e => {
          e.stopPropagation()
          setOpen(true)
        }}
      >
        <AddContentIcon />
        <span>Add files</span>
      </AddFilesTrigger>
      {open && (
        <Modal
          open={open}
          title={selectedRule
            ? `Select unit for ${selectedRule.ruleName || 'this rule'}`
            : `Upload files for ${displayName}`
          }
          onClose={handleClose}
        >
          <RulePickerContent>
            {selectedRule ? (
              <>
                <RulePickerHint>
                  Select which unit you want to upload files for.
                </RulePickerHint>
                <BackButton type='button' onClick={handleBack}>
                  ← Back to rules
                </BackButton>
                {hasBpScope && (
                  <RulePickerCard
                    type='button'
                    onClick={() => openUploadPage(selectedRule, null)}
                  >
                    <RulePickerIndex>BP</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>Whole Building Passport</RulePickerName>
                    </RulePickerInfo>
                    <AddContentIcon />
                  </RulePickerCard>
                )}
                {unitsForRule.map(unit => (
                  <RulePickerCard
                    key={unit.id}
                    type='button'
                    onClick={() => openUploadPage(selectedRule, unit.id)}
                    disabled={unit.uploaded || !unit.isActive}
                  >
                    <RulePickerIndex>
                      {unit.uploaded ? '✓' : '↑'}
                    </RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>{unit.name}</RulePickerName>
                      {!unit.isActive ? (
                        <RulePickerState>Removed unit</RulePickerState>
                      ) : unit.assignee && (
                        <RulePickerState>{unit.assignee}</RulePickerState>
                      )}
                    </RulePickerInfo>
                    {unit.uploaded ? (
                      <RuleUploadedIndicator>Uploaded</RuleUploadedIndicator>
                    ) : (
                      <AddContentIcon />
                    )}
                  </RulePickerCard>
                ))}
                {unitsForRule.length === 0 && (
                  <RulePickerHint>
                    No active units are assigned to this rule. Use Settings to update the checklist assignments.
                  </RulePickerHint>
                )}
              </>
            ) : (
              <>
                <RulePickerHint>
                  Select which rule you want to upload files for. Each rule has different tag requirements.
                </RulePickerHint>
                {isRefreshingOverview && (
                  <RulePickerSkeletonCard>
                    <Skeleton variant='circular' width={28} height={28} />
                    <RulePickerSkeletonContent>
                      <Skeleton variant='text' width='44%' height={24} />
                      <Skeleton variant='text' width='78%' height={18} />
                    </RulePickerSkeletonContent>
                    <Skeleton variant='rounded' width={22} height={22} />
                  </RulePickerSkeletonCard>
                )}
                {!isRefreshingOverview && rules.map((rule, index) => (
                  <RulePickerCard
                    key={rule.groupNo}
                    type='button'
                    onClick={() => handleRuleClick(rule)}
                    disabled={rule.disabled}
                  >
                    <RulePickerIndex>{index + 1}</RulePickerIndex>
                    <RulePickerInfo>
                      <RulePickerName>
                        {rule.ruleName || `Rule ${index + 1}`}
                      </RulePickerName>
                      {rule.tagNames.length > 0 && (
                        <RulePickerTags>
                          {rule.tagNames.map(tag => (
                            <RulePickerTag key={tag}>{tag}</RulePickerTag>
                          ))}
                        </RulePickerTags>
                      )}
                      {rule.disabled && (
                        <RulePickerState>
                          <RuleUploadedBadge>File already uploaded</RuleUploadedBadge>
                        </RulePickerState>
                      )}
                    </RulePickerInfo>
                    {rule.disabled ? (
                      <RuleUploadedIndicator>Uploaded</RuleUploadedIndicator>
                    ) : (
                      <AddContentIcon />
                    )}
                  </RulePickerCard>
                ))}
              </>
            )}
          </RulePickerContent>
        </Modal>
      )}
    </>
  )
}

const AnalyticsFolderProgressBar = ({
  template,
  redirectPath,
  showDetailsOnly = false
}) => {
  const { id } = useParams()
  const bp = useSelector(getSelectedBp)
  const bpFiles = useSelector(selectBpFilesList)
  const bpUnits = useSelector(selectBpUnits)
  const isDefaultTemplate = template.type === 'default'
  const fileCategories = useSelector(selectFileCategories)
  const dispatch = useDispatch()
  const [open, setOpen] = useState(showDetailsOnly)
  const [anchorEl, setAnchorEl] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editContentName, setEditContentName] = useState('')
  const [itemOverrides, setItemOverrides] = useState({})
  const [fileItemTagSelections, setFileItemTagSelections] = useState({})
  const lastBpUnitsLogKeyRef = useRef('')
  const dropdownOpen = Boolean(anchorEl)
  const currentUser = useSelector(getCurrentUser)

  const canEdit = canEditBp(currentUser, bp)
  const isTemplateEditable = canEdit && template.type !== 'default'
  const canUploadFiles = isTemplateEditable
  const canCreateChecklistTasks = Boolean(bp?.hasTaskManagement ?? bp?.has_task_management)
  const templatePercentage = getTemplatePercentage(template, bp, bpFiles)
  const unitNameById = useMemo(
    () =>
      (bpUnits || []).reduce((acc, unit) => {
        if (unit?.id && unit?.name) acc[String(unit.id)] = String(unit.name)
        return acc
      }, {}),
    [bpUnits]
  )

  useEffect(() => {
    if (!SHOULD_LOG_CHECKLIST_TASK_DEBUG) return
    const logKey = JSON.stringify({
      bpId: bp?.id || '',
      count: bpUnits?.length || 0,
      sample: (bpUnits || []).slice(0, 20).map(unit => [unit?.id, unit?.name])
    })

    if (lastBpUnitsLogKeyRef.current === logKey) return
    lastBpUnitsLogKeyRef.current = logKey

    console.groupCollapsed('[Checklist Create Task] BP units store snapshot')
    console.log('bp', {
      bpId: bp?.id,
      bpName: bp?.name
    })
    console.log('bpUnits count', bpUnits?.length || 0)
    console.table(
      (bpUnits || []).slice(0, 20).map(unit => ({
        id: unit?.id,
        name: unit?.name,
        uprn: unit?.uprn
      }))
    )
    console.groupEnd()
  }, [bp?.id, bp?.name, bpUnits])

  useEffect(() => {
    if (showDetailsOnly) {
      setOpen(true)
    }
  }, [showDetailsOnly])

  useEffect(() => {
    setItemOverrides({})
  }, [template?.id])

  useEffect(() => {
    if (!open || !bp?.id || !template?.id) return

    const templateItems =
      template?.items ||
      template?.analyticsTemplateItems ||
      template?.analytics_template_items ||
      []
    const templateFileTypes =
      template?.fileTypes ||
      template?.file_types ||
      []

    const templateFileItems = templateItems.filter(
      item => (item.itemType || item.item_type || 'file') === 'file' && item.id
    )
    const fileItemTypeIds = new Set(
      templateFileItems
        .map(item => item.fileTypeId || item.file_type_id)
        .filter(Boolean)
        .map(String)
    )
    const fallbackFileItems = templateFileTypes
      .filter(fileType => fileType?.id && !fileItemTypeIds.has(String(fileType.id)))
      .map(fileType => ({
        id: fileType.id,
        fileTypeId: fileType.id,
        file_type_id: fileType.id
      }))
    const fileItems = [...templateFileItems, ...fallbackFileItems]

    let active = true
    fileItems.forEach(item => {
      if (itemOverrides[item.id]?.checklistOverview || item?.checklistOverview || item?.checklist_overview) return
      getChecklistItemOverview({ bpId: bp.id, templateId: template.id, itemId: item.id })
        .then(({ data }) => {
          if (!active) return
          const updatedItem = data?.data
          if (updatedItem?.id) {
            setItemOverrides(prev => ({ ...prev, [updatedItem.id]: updatedItem }))
          }
        })
        .catch(() => {})
    })

    return () => { active = false }
  }, [open, bp?.id, template?.id])

  useEffect(() => {
    const templateItems =
      template?.items ||
      template?.analyticsTemplateItems ||
      template?.analytics_template_items ||
      []

    const itemSelections = templateItems
      .filter(item => (item.itemType || item.item_type || 'file') === 'file')
      .reduce((acc, item) => {
        const fileTypeId = item.fileTypeId || item.file_type_id
        const tagIds = (
          item.bpTagRequirements ||
          item.bp_tag_requirements ||
          item.tagRequirements ||
          item.tag_requirements ||
          []
        )
          .map(
            tag =>
              tag?.fileTagId ||
              tag?.file_tag_id ||
              tag?.file_tag?.id ||
              tag?.fileTag?.id
          )
          .filter(Boolean)
        if (!fileTypeId) return acc
        acc[String(fileTypeId)] = Array.from(new Set(tagIds.map(String)))
        return acc
      }, {})

    setFileItemTagSelections(itemSelections)
  }, [template])

  const checklistFileItemsByType = useMemo(() => {
    const templateItems =
      template?.items ||
      template?.analyticsTemplateItems ||
      template?.analytics_template_items ||
      []

    return templateItems
      .filter(item => (item.itemType || item.item_type || 'file') === 'file')
      .reduce((acc, item) => {
        const fileTypeId = item.fileTypeId || item.file_type_id
        if (!fileTypeId || acc[String(fileTypeId)]) return acc
        acc[String(fileTypeId)] = itemOverrides[item.id] || itemOverrides[String(fileTypeId)] || item
        return acc
      }, {})
  }, [itemOverrides, template])

  const openMenu = e => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  const closeEditModal = () => {
    setEditContentName('')
    setModalOpen(false)
  }

  const openEditModal = name => () => {
    setEditContentName(name)
    setModalOpen(true)
  }

  const handleTemplateDetach = e => {
    e.stopPropagation()
    dispatch(
      detachAnalyticsTemplateRoutine({
        bpId: bp.id,
        templateId: template.id,
        shouldUpdateBp: true
      })
    )
  }

  const ensureBpTagsLoaded = e => {
    e.stopPropagation()
    if (bp?.id) {
      dispatch(getFileTagsForBpRoutine({ bpId: bp.id }))
    }
  }

  const dataValues = useMemo(() => {
    let result = []
    const values = pick(
      ['hasUprn', 'hasAddress', 'hasOwner', 'hasAgent', 'hasBuildingTypes'],
      template
    )
    for (const key in values) {
      if (values[key]) {
        result.push(key)
      }
    }
    return result
  }, [template, bp])

  const filesValues = useMemo(() => {
    if (fileCategories && isNotNilOrEmpty(template.fileTypes)) {
      const categories = fileCategories.map(category => {
        return {
          id: category.id,
          label: category.name,
          values:
            template.fileTypes.filter(
              type => type.fileCategoryId === category.id
            ) || []
        }
      })

      return categories.map(category => {
        if (isNotNilOrEmpty(category.values)) {
          return (
            <div key={`${category.label}-analytics`}>
              <CategoryName>{category.label}</CategoryName>
              {category.values.map(value => {
                const actualFilesForType = (bpFiles || []).filter(
                  f => !f.deletedAt && f.fileTypeId === value.id
                ).length
                const filesCount = Math.max(value?.filesCount || 0, actualFilesForType)
                const isCompleted = checkAnalyticsCondition(
                  value.id,
                  bp,
                  bpFiles
                )
                const checklistItem = checklistFileItemsByType[String(value.id)] || itemOverrides[String(value.id)]
                const displayName = checklistItem?.title || value.name
                const configuredTagNames =
                  getChecklistItemTagNames(checklistItem)
                const legacyConfiguredTagIds =
                  fileItemTagSelections[String(value.id)] || []
                const configuredTagsCount = checklistItem?.id
                  ? configuredTagNames.length
                  : legacyConfiguredTagIds.length
                const checklistOverview =
                  checklistItem?.checklistOverview ||
                  checklistItem?.checklist_overview
                const overviewEntries = checklistOverview?.entries || []
                const tagRequirements =
                  checklistItem?.bp_tag_requirements ||
                  checklistItem?.bpTagRequirements ||
                  checklistItem?.tag_requirements ||
                  checklistItem?.tagRequirements ||
                  []
                const tagIdsByGroup = tagRequirements.reduce((acc, req) => {
                  const gNo = Number(req?.group_no ?? req?.groupNo ?? 0)
                  const tagId =
                    req?.file_tag_id ||
                    req?.fileTagId ||
                    req?.file_tag?.id ||
                    req?.fileTag?.id
                  if (!acc[gNo]) acc[gNo] = []
                  if (tagId && !acc[gNo].includes(String(tagId))) {
                    acc[gNo].push(String(tagId))
                  }
                  return acc
                }, {})
                const uploadedByGroup = overviewEntries.reduce((acc, entry) => {
                  const gNo = Number(entry?.groupNo ?? entry?.group_no ?? 0)
                  if (!acc[gNo]) acc[gNo] = false
                  if (isChecklistEntryUploaded(entry)) {
                    acc[gNo] = true
                  }
                  return acc
                }, {})
                const ruleGroupsMap = {}
                getChecklistGroupNumbers(checklistItem, overviewEntries).forEach(
                  gNo => {
                    const overviewEntry = overviewEntries.find(
                      entry =>
                        Number(entry?.groupNo ?? entry?.group_no ?? 0) === gNo
                    )
                    ruleGroupsMap[gNo] = {
                      groupNo: gNo,
                      ruleName:
                        overviewEntry?.ruleName ||
                        overviewEntry?.rule_name ||
                        '',
                      tagNames:
                        overviewEntry?.tagNames ||
                        overviewEntry?.tag_names ||
                        [],
                      tagIds:
                        tagIdsByGroup[gNo] ||
                        overviewEntry?.tagIds ||
                        overviewEntry?.tag_ids ||
                        [],
                      assigneeIds: getEntryAssigneeIds(overviewEntry),
                      assigneeUsers: getEntryAssigneeUsers(overviewEntry),
                      disabled: Boolean(uploadedByGroup[gNo]),
                      taskId: overviewEntry?.task?.id || null,
                      uploadUrl:
                        overviewEntry?.uploadUrl ||
                        overviewEntry?.upload_url ||
                        null
                    }
                  }
                )
                const rulesList = Object.values(ruleGroupsMap)
                const assignedEntries = overviewEntries.filter(
                  e => !!(e?.assignee?.label || e?.assignee?.email)
                )
                const totalEntriesCount = assignedEntries.length
                const uploadedEntriesCount = assignedEntries.filter(
                  e => isChecklistEntryUploaded(e)
                ).length
                const capacityDenominator = totalEntriesCount || overviewEntries.length
                const effectiveUploaded = capacityDenominator > 0
                  ? Math.max(uploadedEntriesCount, Math.min(filesCount, capacityDenominator))
                  : filesCount
                const effectiveTotal = capacityDenominator || filesCount
                const overviewProgress = effectiveTotal > 0
                  ? (effectiveUploaded / effectiveTotal) * 100
                  : filesCount > 0 ? 100 : 0
                const isChecklistItem = Boolean(checklistItem?.id)
                const hasChecklistOverview = overviewEntries.length > 0
                const isChecklistComplete = effectiveTotal > 0
                  ? effectiveUploaded >= effectiveTotal
                  : isChecklistItem && (Boolean(value?.fileExists) || filesCount > 0)
                const statusChecked = isChecklistItem
                  ? isChecklistComplete
                  : Boolean(value?.fileExists) || overviewProgress >= 100 || filesCount > 0

                const handleEditIconClick = e => {
                  if (e && e.stopPropagation) e.stopPropagation()
                  const safeId = id || (bp && bp.id)
                  const safePath = redirectPath || PRIVATE_PATHS.bpList

                  if (value.name === 'Building plan') {
                    redirect(`${safePath}/${safeId}/floorplans`)
                  } else {
                    redirect(`${safePath}/${safeId}/other`)
                  }
                }

                const nameContent = (
                  <SingleItemName
                    style={{
                      cursor: isTemplateEditable ? 'pointer' : 'default'
                    }}
                  >
                    <Status checked={statusChecked} $progress={overviewProgress}>
                      {statusChecked ? <CheckedStatusIcon /> : <UncheckedStatusIcon />}
                    </Status>
                    <FileTextColumn>
                      <div>{displayName}</div>
                      {isChecklistItem && hasChecklistOverview ? (
                        <ChecklistOverviewSummary
                          item={checklistItem}
                          filesCount={filesCount}
                          bpId={bp.id}
                          fileTypeId={value.id}
                          categoryId={category.id}
                          templateId={template.id}
                          leadingContent={
                            configuredTagsCount > 0 ? (
                              <ChecklistItemTagsPreviewModal
                                title={`${displayName} tags`}
                                tagNames={configuredTagNames}
                              >
                                <TagPreviewCount>
                                  {configuredTagsCount} tag
                                  {configuredTagsCount === 1 ? '' : 's'} configured
                                </TagPreviewCount>
                              </ChecklistItemTagsPreviewModal>
                            ) : null
                          }
                        />
                      ) : (
                        <LegacySummaryWrap>
                          {configuredTagsCount > 0 && (
                            <TagPreviewCount>
                              {configuredTagsCount} tag
                              {configuredTagsCount === 1 ? '' : 's'} configured
                            </TagPreviewCount>
                          )}
                          {effectiveTotal > 0 ? (
                            <AlreadyUploadedFilesOfTypeModal
                              bpId={bp.id}
                              type={value.id}
                              categoryId={category.id}
                              templateId={template.id}
                            >
                              <LegacyFilesCount $progress={overviewProgress}>
                                {effectiveUploaded}/{effectiveTotal} file{effectiveTotal === 1 ? '' : 's'} uploaded
                              </LegacyFilesCount>
                            </AlreadyUploadedFilesOfTypeModal>
                          ) : filesCount > 0 ? (
                            <AlreadyUploadedFilesOfTypeModal
                              bpId={bp.id}
                              type={value.id}
                              categoryId={category.id}
                              templateId={template.id}
                            >
                              <LegacyFilesCount $progress={100}>
                                {filesCount}/{filesCount} file{filesCount === 1 ? '' : 's'} uploaded
                              </LegacyFilesCount>
                            </AlreadyUploadedFilesOfTypeModal>
                          ) : null}
                        </LegacySummaryWrap>
                      )}
                    </FileTextColumn>
                  </SingleItemName>
                )

                return (
                  <SingleItem key={value.id}>
                    {nameContent}

                    <SingleItemAction>
                      {canUploadFiles && !isChecklistComplete && (
                        <>
                          {rulesList.length > 0 ? (
                            <RulePickerUpload
                              rules={rulesList}
                              bpId={bp.id}
                              categoryId={category.id}
                              typeId={value.id}
                              templateId={template.id}
                              itemId={checklistItem?.id}
                              displayName={displayName}
                              unitNameById={unitNameById}
                              onOverviewLoaded={updatedItem => {
                                if (!updatedItem?.id) return
                                const ftId = updatedItem?.fileTypeId || updatedItem?.file_type_id
                                setItemOverrides(prev => ({
                                  ...prev,
                                  [updatedItem.id]: updatedItem,
                                  ...(ftId ? { [String(ftId)]: updatedItem } : {})
                                }))
                              }}
                            />
                          ) : isCompleted && !isChecklistItem && isTemplateEditable ? (
                            <ActionIconWrapper onClick={handleEditIconClick}>
                              <span>edit</span>
                              <EditIcon />
                            </ActionIconWrapper>
                          ) : (
                            <AddMultipleFilesModal
                              initialType={value.id}
                              initialCategory={category.id}
                              externalBpId={bp.id}
                              templateId={template.id}
                            >
                              <AddFilesTrigger
                                type='button'
                                aria-label={`Add files for ${displayName}`}
                                title='Add files'
                              >
                                <AddContentIcon />
                                <span>Add files</span>
                              </AddFilesTrigger>
                            </AddMultipleFilesModal>
                          )}
                        </>
                      )}
                      {isTemplateEditable && (
                        <AssignInlineWrap onClick={e => e.stopPropagation()}>
                          <AssignChecklistItemModal
                            bpId={bp.id}
                            templateId={template.id}
                            item={checklistItem || { title: displayName, fileTypeId: value.id, file_type_id: value.id }}
                            itemIdOverride={checklistItem?.id ? undefined : value.id}
                            onAssigned={updatedItem => {
                              if (!updatedItem?.id) return
                              const ftId = updatedItem?.fileTypeId || updatedItem?.file_type_id
                              setItemOverrides(prev => ({
                                ...prev,
                                [updatedItem.id]: updatedItem,
                                ...(ftId ? { [String(ftId)]: updatedItem } : {})
                              }))
                            }}
                          >
                            <SettingsTrigger
                              type='button'
                              aria-label={`Configure ${displayName}`}
                              title='Settings'
                            >
                              <SettingsOutlinedIcon style={{ fontSize: 18 }} />
                              <SettingsLabel>Settings</SettingsLabel>
                            </SettingsTrigger>
                          </AssignChecklistItemModal>
                        </AssignInlineWrap>
                      )}
                      {isTemplateEditable && !isChecklistComplete && bp?.hasTaskManagement && (
                        <ChecklistTaskTrigger
                          rules={rulesList}
                          bpId={bp.id}
                          bpName={bp.name}
                          categoryId={category.id}
                          typeId={value.id}
                          template={template}
                          item={checklistItem || {
                            id: value.id,
                            title: displayName,
                            fileTypeId: value.id,
                            file_type_id: value.id
                          }}
                          displayName={displayName}
                          overviewEntries={overviewEntries}
                          unitNameById={unitNameById}
                          activeUnits={bpUnits}
                        />
                      )}
                    </SingleItemAction>
                  </SingleItem>
                )
              })}
            </div>
          )
        } else {
          return null
        }
      })
    }
    return null
  }, [
    template,
    fileCategories,
    bp,
    bpFiles,
    canEdit,
    redirectPath,
    id,
    checklistFileItemsByType,
    unitNameById
  ])

  const modalContent = useMemo(() => {
    switch (editContentName) {
      case 'hasUprn':
        return <EditGeneralInfo closeModal={closeEditModal} />
      case 'hasAddress':
        return <EditAddress closeModal={closeEditModal} />
      case 'hasBuildingTypes':
        return <EditBuildingTypes closeModal={closeEditModal} />
      case 'hasAgent':
        return (
          <AddAssignmentModal
            initialUserType='agent'
            bp={bp}
            closeModal={closeEditModal}
          />
        )
      case 'hasOwner':
        return (
          <AddAssignmentModal
            initialUserType='owner'
            bp={bp}
            closeModal={closeEditModal}
          />
        )
      default:
        return <div />
    }
  }, [editContentName, bp])

  return (
    <Wrapper>
      {!showDetailsOnly && (
        <>
          <TemplateName>
            <div>{template.name}</div>
            {!isDefaultTemplate && (
              <IconWrapper>
                <MoreIcon onClick={openMenu} />
                <Menu
                  keepMounted
                  anchorEl={anchorEl}
                  style={{ marginTop: '10px' }}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                  open={dropdownOpen}
                  onClose={handleCloseMenu}
                >
                  <MenuItem
                    onClick={() =>
                      redirect(PRIVATE_PATHS.editAnalytics + `#${template.id}`)
                    }
                  >
                    Edit template
                  </MenuItem>
                  <MenuItem onClick={handleTemplateDetach}>
                    Remove template
                  </MenuItem>
                </Menu>
              </IconWrapper>
            )}
          </TemplateName>
          <TemplateType>
            {isDefaultTemplate ? 'default' : 'custom'}
          </TemplateType>
          <ItemWrapper onClick={() => setOpen(prev => !prev)}>
            <ProgressBarWrapper>
              <Percentage>{templatePercentage}%</Percentage>
              <ProgressBar width={templatePercentage} />
            </ProgressBarWrapper>
            <ActionsWrapper>
              <CollapseIcon open={open} />
            </ActionsWrapper>
          </ItemWrapper>
        </>
      )}
      <Collapse in={open}>
        <DetailsWrapper open={open}>
          <SectionTitle>data</SectionTitle>
          {dataValues.map(item => {
            const isAssigned =
              (item === 'hasAgent' || item === 'hasOwner') &&
              checkAnalyticsCondition(item, bp)
            const isChecked = checkAnalyticsCondition(item, bp)

            const handleItemClick = e => {
              if (e && e.stopPropagation) e.stopPropagation()
              if (!isTemplateEditable) return

              if (isAssigned) {
                redirect(`${redirectPath}/accessControl`)
              } else {
                openEditModal(item)()
              }
            }

            return (
              <SingleItem key={item}>
                <SingleItemName
                  onClick={handleItemClick}
                  style={{ cursor: isTemplateEditable ? 'pointer' : 'default' }}
                >
                  <Status checked={isChecked}>
                    {isChecked ? <CheckedStatusIcon /> : <UncheckedStatusIcon />}
                  </Status>
                  {TEMPLATE_ITEMS_NAMES[item]}
                </SingleItemName>
                <SingleItemAction>
                  {isTemplateEditable && (
                    <ActionIconWrapper onClick={handleItemClick}>
                      {isChecked ? (
                        <>
                          <span>edit</span>
                          <EditIcon />
                        </>
                      ) : (
                        <>
                          <span>add</span>
                          <AddContentIcon />
                        </>
                      )}
                    </ActionIconWrapper>
                  )}
                </SingleItemAction>
              </SingleItem>
            )
          })}
          {isNotNilOrEmpty(template.fileTypes) && (
            <>
              <FileSectionTitle>files</FileSectionTitle>
              {filesValues}
            </>
          )}
        </DetailsWrapper>
      </Collapse>
      <Modal open={modalOpen} title='Edit' onClose={closeEditModal}>
        {modalContent}
      </Modal>
    </Wrapper>
  )
}

export default AnalyticsFolderProgressBar

const Wrapper = styled.div`
  margin: 7px 0;
`

const ItemWrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const TemplateName = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const TemplateType = styled.div`
  color: ${({ theme }) => theme.colors.grey[600]};
  text-transform: uppercase;
  font-size: 12px;
`

const ProgressBarWrapper = styled.div`
  width: 100%;
  position: relative;
  background-color: ${({ theme }) => theme.colors.backgroundColor};
  border-radius: 4px;
`

const ProgressBar = styled.div`
  width: ${({ width }) => width}%;
  height: 5px;
  background-color: ${({ width }) => color(width)};
  border-radius: 4px;
`

const Percentage = styled.div`
  position: absolute;
  right: 0;
  font-size: 12px;
  top: -20px;
`

const DetailsWrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.grey[400]};
  padding: 20px;
  transition: all 0.3s;
  margin: ${({ open }) => (open ? '10px 0' : 0)};
`

const SingleItem = styled.div`
  margin-bottom: 5px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  > span {
    display: inline-block;
    color: ${({ theme }) => theme.colors.secondary};
    margin-right: 10px;
  }
`

const SectionTitle = styled.div`
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 10px;
`

const CollapseIcon = styled(KeyboardArrowDownOutlinedIcon)`
  transition: all 0.3s;
  cursor: pointer;
  transform: rotate(${({ open }) => (open ? '180deg' : '0')});
  margin-left: 10px;
`

const FileSectionTitle = styled.div`
  text-transform: uppercase;
  margin: 30px 0 10px;
`

const CategoryName = styled.div`
  color: ${({ theme }) => theme.colors.secondary};
  margin: 15px 0 10px;
`

const getStatusColor = (checked, progress) => {
  if (typeof progress === 'number') {
    if (progress >= 100) return '#208553'
    if (progress > 0) return '#b27300'
    return '#b00020'
  }
  return checked ? '#208553' : '#b00020'
}

const getProgressBadgeColors = progress => {
  if (progress >= 100) return { bg: 'rgba(61, 179, 122, 0.14)', color: '#208553' }
  if (progress > 0) return { bg: 'rgba(255, 170, 0, 0.14)', color: '#b27300' }
  return { bg: 'rgba(204, 34, 0, 0.1)', color: '#b00020' }
}

const Status = styled.div`
  color: ${({ checked, $progress }) => getStatusColor(checked, $progress)};
  margin-right: 10px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const CheckedStatusIcon = styled(CheckBoxOutlinedIcon)`
  font-size: 22px !important;
`

const UncheckedStatusIcon = styled(CheckBoxOutlineBlankOutlinedIcon)`
  font-size: 22px !important;
`

const ActionsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const MoreIcon = styled(MoreVertIcon)`
  color: ${({ theme }) => theme.colors.grey[600]};
  cursor: pointer;
  font-size: 20px !important;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`

const SingleItemName = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    align-items: flex-start;
    line-height: 1.4;
  }
`

const FileTextColumn = styled.div`
  min-width: 0;
`

const TagPreviewCount = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 12px;
  background: rgba(51, 190, 204, 0.14);
  color: #156f78;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

const LegacySummaryWrap = styled.div`
  margin-top: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
`

const LegacyFilesCount = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 5px 12px;
  background: ${({ $progress = 0 }) => getProgressBadgeColors($progress).bg};
  color: ${({ $progress = 0 }) => getProgressBadgeColors($progress).color};
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
`

const AddFilesTrigger = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  flex: 0 0 28px;

  &:hover {
    background: rgba(51, 190, 204, 0.1);
  }

  span {
    display: none;
  }
`

const CreateTaskTrigger = styled(AddFilesTrigger)`
  color: #208553;

  &:hover {
    background: rgba(61, 179, 122, 0.1);
    color: #16643f;
  }
`

const BackButton = styled.button`
  border: none;
  background: none;
  color: #17818a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`

const RulePickerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const RulePickerHint = styled.div`
  font-size: 13px;
  color: #6f7785;
  line-height: 1.5;
`

const RulePickerToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const RulePickerSearchInput = styled.input`
  width: 100%;
  height: 38px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  padding: 0 12px;
  color: #172033;
  font-size: 13px;
  outline: none;

  &:focus {
    border-color: #33becc;
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.12);
  }

  &::placeholder {
    color: #9aa3b2;
  }
`

const RulePickerBulkActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const RulePickerUtilityButton = styled.button`
  min-height: 32px;
  border: 1px solid #dfe3ea;
  border-radius: 8px;
  background: #fff;
  color: #17818a;
  font-size: 12px;
  font-weight: 700;
  padding: 0 10px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #33becc;
    background: rgba(51, 190, 204, 0.05);
  }

  &:disabled {
    cursor: not-allowed;
    color: #a5adba;
    background: #f8fafc;
  }
`

const RulePickerSelectedCount = styled.div`
  color: #6f7785;
  font-size: 12px;
  font-weight: 600;
`

const RulePickerUnitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const RulePickerCard = styled.button`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid ${({ $selected }) => ($selected ? '#33becc' : '#ebedf1')};
  border-radius: 8px;
  background: ${({ $selected }) => ($selected ? 'rgba(51, 190, 204, 0.05)' : '#fff')};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #33becc;
    background: rgba(51, 190, 204, 0.04);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    background: #f8fafc;
  }
`

const RulePickerSkeletonCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid #ebedf1;
  border-radius: 8px;
  background: #fff;
`

const RulePickerSkeletonContent = styled.div`
  flex: 1;
  min-width: 0;
`

const RulePickerIndex = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(51, 190, 204, 0.12);
  color: #156f78;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const RulePickerInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const RulePickerName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #172033;
`

const RulePickerTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`

const RulePickerTag = styled.span`
  padding: 3px 8px;
  border-radius: 999px;
  background: #f3f6fb;
  color: #33415d;
  font-size: 11px;
  font-weight: 600;
`

const RulePickerState = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #6f7785;
`

const RulePickerSelection = styled.div`
  width: 32px;
  height: 32px;
  color: ${({ $selected }) => ($selected ? '#208553' : '#98a1af')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 22px;
  }
`

const RulePickerFooter = styled.div`
  position: sticky;
  bottom: -1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0 0;
  background: #fff;
  border-top: 1px solid #eef1f5;
`

const RulePickerPrimaryButton = styled.button`
  min-height: 38px;
  border: 1px solid #208553;
  border-radius: 8px;
  background: rgba(61, 179, 122, 0.06);
  color: #208553;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;

  svg {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    border-color: #16643f;
    background: rgba(61, 179, 122, 0.14);
    color: #16643f;
  }

  &:disabled {
    border-color: #dfe3ea;
    background: #f8fafc;
    color: #a5adba;
    cursor: not-allowed;
  }
`

const RuleUploadedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 4px 8px;
  background: rgba(61, 179, 122, 0.14);
  color: #208553;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
`

const RuleUploadedIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 6px 10px;
  background: rgba(61, 179, 122, 0.14);
  color: #208553;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
`

const SingleItemAction = styled.div`
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 6px;
  margin-top: 4px;

  @media (max-width: 768px) {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    margin-top: 0;
  }
`

const AssignInlineWrap = styled.div`
  display: inline-flex;
`

const SettingsTrigger = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
  flex: 0 0 28px;

  &:hover {
    background: rgba(51, 190, 204, 0.1);
  }
`

const SettingsLabel = styled.span`
  display: none;
`

const EditIcon = styled(CreateOutlinedIcon)`
  font-size: 20px !important;
  color: ${({ theme }) => theme.colors.secondary};
`

const AddContentIcon = styled(NoteAddOutlinedIcon)`
  font-size: 18px !important;
  color: ${({ theme }) => theme.colors.secondary};
`

const CreateTaskIcon = styled(AddTaskOutlinedIcon)`
  font-size: 18px !important;
  color: currentColor;
`

const ActionIconWrapper = styled.div`
  display: flex;
  position: relative;
  top: 0;
  right: 0;
  align-items: center;
  cursor: pointer;

  span {
    transition: all 0.3s;
    opacity: 0;
  }

  &:hover {
    span {
      opacity: 1;
      background-color: ${({ theme }) => theme.colors.grey[400]};
    }
  }

  span {
    display: inline-block;
    margin-right: 3px;
    font-size: 13px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.secondary};
  }
`
